/**
 * RecoveryCodesDisplay — the one-time recovery-codes surface shared by the
 * post-enroll full-screen page (`MfaRecoveryCodesPage`) and the in-card
 * regenerate flow (`MfaSettingsCard`). Renders the codes with copy / download /
 * print, a reload/close guard, and a must-confirm checkbox that gates the
 * caller-supplied continue action.
 *
 * SECURITY: codes are display-only, held in the parent's React state, and never
 * logged or sent to telemetry. `print` escapes each code before writing it into
 * the print window (defense-in-depth — codes are server-generated base32 today,
 * but the escaping means a format change can't become reflected XSS).
 */

import { useEffect, useState, type ReactNode } from 'react';
import { KeyRound, Copy, Check, Download, Printer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';

const HTML_ESCAPES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (c) => HTML_ESCAPES[c]);
}

/**
 * Identity a printed / downloaded sheet needs to be self-describing.
 *
 * These artifacts outlive the browser session: they end up in a drawer or a
 * password manager months later, next to sheets from other services and
 * possibly other accounts. Without the service, the account, and the issue
 * date, the holder cannot tell WHAT it unlocks, WHOSE it is, or whether a newer
 * set has superseded it (regenerating invalidates every earlier sheet).
 */
export interface RecoveryCodesMeta {
  /** Which account these unlock — email preferred; it's what the user recognises. */
  account?: string;
  /** ISO timestamp the set was issued, from the server. */
  generatedAt?: string;
}

const SERVICE_NAME = 'Fias';

/**
 * The one-time / consumption rule, stated the same way everywhere.
 *
 * "Works once" alone under-warns: it reads as "reusable until it stops working".
 * Saying the code is CONSUMED on use tells the holder to strike it off the
 * sheet, which is the behaviour that keeps a paper copy accurate.
 */
const ONE_TIME_NOTICE =
  'Each code works once. Using a code consumes it — cross it off this list once used.';

function formatGeneratedAt(generatedAt?: string): string | null {
  if (!generatedAt) return null;
  const d = new Date(generatedAt);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, { dateStyle: 'long', timeStyle: 'short' });
}

/**
 * Open a print window with just the codes. Returns false if the popup was blocked.
 *
 * SECURITY: this sheet carries the recovery codes and NOTHING else that could
 * compromise the factor — never render the TOTP secret, the otpauth URI, or the
 * enrolment QR here. Those reconstruct the authenticator itself, so putting them
 * on a printed page would turn a fallback artifact into a full second factor
 * sitting in a drawer. It also deliberately carries no app chrome, styles, or
 * scripts — the window is written from this string alone.
 */
export function printRecoveryCodes(codes: string[], meta: RecoveryCodesMeta = {}): boolean {
  const win = window.open('', '_blank', 'width=480,height=640');
  if (!win) return false;
  const items = codes.map((c) => `<li>${escapeHtml(c)}</li>`).join('');
  const issued = formatGeneratedAt(meta.generatedAt);
  const metaLines = [
    meta.account ? `<p class="meta">Account: <strong>${escapeHtml(meta.account)}</strong></p>` : '',
    issued ? `<p class="meta">Generated: ${escapeHtml(issued)}</p>` : '',
  ].join('');
  win.document.write(
    `<html><head><title>${SERVICE_NAME} recovery codes</title>` +
      `<style>body{font-family:sans-serif;padding:24px}h1{font-size:16px;margin:0 0 4px}` +
      `ol{font-family:monospace}li{font-size:18px;letter-spacing:2px;margin:6px 0}` +
      `p{font-size:12px;color:#555}.meta{margin:2px 0}` +
      `.notice{margin-top:12px;font-weight:600;color:#333}</style></head><body>` +
      `<h1>${SERVICE_NAME} — two-factor recovery codes</h1>` +
      metaLines +
      `<p class="notice">${escapeHtml(ONE_TIME_NOTICE)}</p>` +
      `<ol>${items}</ol>` +
      `<p>Keep this sheet somewhere safe. Generating a new set invalidates every code above.</p>` +
      `</body></html>`,
  );
  win.document.close();
  win.focus();
  win.print();
  return true;
}

/** Same content and the same security rule as the printed sheet, as a .txt file. */
export function downloadRecoveryCodes(codes: string[], meta: RecoveryCodesMeta = {}): void {
  const issued = formatGeneratedAt(meta.generatedAt);
  const header = [
    `${SERVICE_NAME} — two-factor recovery codes`,
    meta.account ? `Account: ${meta.account}` : null,
    issued ? `Generated: ${issued}` : null,
    '',
    ONE_TIME_NOTICE,
    '',
  ]
    .filter((l) => l !== null)
    .join('\n');
  const footer = `\nKeep this file somewhere safe. Generating a new set invalidates every code above.\n`;
  const text = `${header}${codes.join('\n')}\n${footer}`;
  const blob = new Blob([text], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'fias-recovery-codes.txt';
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Defer revocation so the browser has finished reading the blob (revoking
  // synchronously after click() can cancel the download in some browsers).
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export function RecoveryCodesDisplay({
  codes,
  account,
  generatedAt,
  renderAction,
}: {
  codes: string[];
  /** Account identifier stamped on the printed / downloaded copy. */
  account?: string;
  /** ISO timestamp the set was issued, stamped on the offline copy. */
  generatedAt?: string;
  /** The forward CTA, gated on whether the user confirmed they saved the codes. */
  renderAction: (confirmed: boolean) => ReactNode;
}) {
  const [copied, setCopied] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [printBlocked, setPrintBlocked] = useState(false);

  const canCopy =
    typeof navigator !== 'undefined' && typeof navigator.clipboard?.writeText === 'function';

  // These codes are shown exactly once and can't be recovered. Warn on a
  // reload / tab-close until the user confirms they've saved them. (SPA
  // navigation can't trigger this — the full-screen page has no nav chrome, and
  // the in-card flow keeps a live session, so there is no involuntary redirect.)
  useEffect(() => {
    if (confirmed) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [confirmed]);

  const copy = async () => {
    // Only report success on a real write — never a false "Copied" when the
    // Clipboard API is unavailable (insecure context / older browser).
    if (!canCopy) return;
    try {
      await navigator.clipboard.writeText(codes.join('\n'));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Denied — download / print remain.
    }
  };

  const meta = { account, generatedAt };

  const print = () => {
    if (!printRecoveryCodes(codes, meta)) setPrintBlocked(true);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3 rounded-md bg-amber-500/10 border border-amber-500/20 p-3">
        <KeyRound className="h-5 w-5 flex-shrink-0 text-amber-600 dark:text-amber-400" />
        <p className="text-sm text-amber-700 dark:text-amber-300">
          Save these recovery codes now — <strong>they won’t be shown again</strong>. Each one works
          once if you lose access to your authenticator app.
        </p>
      </div>

      <ul className="grid grid-cols-2 gap-2" data-testid="recovery-codes">
        {codes.map((c) => (
          <li
            key={c}
            className="rounded-md border border-border bg-muted/40 px-3 py-2 text-sm font-mono tracking-wider text-center"
          >
            {c}
          </li>
        ))}
      </ul>

      <div className="flex flex-wrap gap-2">
        {canCopy && (
          <Button size="sm" variant="outline" onClick={copy}>
            {copied ? <Check className="h-4 w-4 mr-1.5" /> : <Copy className="h-4 w-4 mr-1.5" />}
            {copied ? 'Copied' : 'Copy'}
          </Button>
        )}
        <Button size="sm" variant="outline" onClick={() => downloadRecoveryCodes(codes, meta)}>
          <Download className="h-4 w-4 mr-1.5" /> Download
        </Button>
        <Button size="sm" variant="outline" onClick={print}>
          <Printer className="h-4 w-4 mr-1.5" /> Print
        </Button>
      </div>

      {printBlocked && (
        <p className="text-xs text-muted-foreground">
          Your browser blocked the print window — use Download or Copy to save your codes instead.
        </p>
      )}

      <div className="flex items-start gap-3 pt-1">
        <Checkbox
          id="mfa-codes-saved"
          checked={confirmed}
          onCheckedChange={(v) => setConfirmed(v === true)}
          className="mt-0.5"
        />
        <label htmlFor="mfa-codes-saved" className="text-sm text-muted-foreground cursor-pointer">
          I’ve saved my recovery codes somewhere safe.
        </label>
      </div>

      {renderAction(confirmed)}
    </div>
  );
}
